<template>
  <section class="listing">
    <div class="header">
      <div class="title">Produtos</div>
      <button @click="$router.push('/product/new')" class="button-new"><i class="fas fa-plus"></i><span>NOVO</span></button>
    </div>
    <transition name="show" mode="out-in">
      <i v-if="loading" class="fas fa-spinner loading-big"></i>
      <div v-if="!loading">
        <div v-for="prod in products" :key="prod.id" class="list product-row">
          <div class="product-content">
            <div class="product-info">
              <div class="product-platform">
                {{ platformLabel(prod.platform) }}
                <span class="product-status" :class="{ inactive: !prod.active }">
                  {{ prod.active ? 'ATIVO' : 'INATIVO' }}
                </span>
              </div>
              <div class="product-desc">{{ prod.desc || prod.name || '-' }}</div>
            </div>
            <div class="periods-list">
              <div v-for="per in prod.periods" :key="per.id" class="period-line">
                <span class="period-name">{{ periodLabel(per) }}</span>
                <span class="period-validity">{{ per.expiration ? per.expiration : '-' }}</span>
                <span class="period-values">
                  <span v-if="per.price != null && per.price > 0" class="period-value">
                    <img src="/img/bra.png" alt="Real" class="currency-flag" title="Real (BRL)" />
                    <template v-if="per.promotion && (per.type === 'percent' || per.type === 'fixed')">
                      <span class="price-original">R$ {{ (per.price || 0).toLocaleString('pt-br', { minimumFractionDigits: 2 }) }}</span>
                      <span class="price-promo">→ R$ {{ promotionPrice(per).toLocaleString('pt-br', { minimumFractionDigits: 2 }) }}</span>
                    </template>
                    <template v-else>
                      R$ {{ (per.price || 0).toLocaleString('pt-br', { minimumFractionDigits: 2 }) }}
                    </template>
                  </span>
                  <span v-if="per.priceUsd != null && per.priceUsd > 0" class="period-value">
                    <img src="/img/eua.png" alt="Dólar" class="currency-flag" title="Dólar (USD)" />
                    <template v-if="per.promotion && (per.type === 'percent' || per.type === 'fixed')">
                      <span class="price-original">US$ {{ (per.priceUsd || 0).toLocaleString('pt-br', { minimumFractionDigits: 2 }) }}</span>
                      <span class="price-promo">→ US$ {{ promotionPriceUsd(per).toLocaleString('pt-br', { minimumFractionDigits: 2 }) }}</span>
                    </template>
                    <template v-else>
                      US$ {{ (per.priceUsd || 0).toLocaleString('pt-br', { minimumFractionDigits: 2 }) }}
                    </template>
                  </span>
                </span>
              </div>
            </div>
          </div>
          <button v-if="prod.periods && prod.periods.length" @click="editProduct(prod)" class="button-edit" title="Alterar"><i class="fas fa-pen"></i></button>
        </div>
      </div>
    </transition>
    <Product v-if="product || productProduto" :id="product || (productProduto ? 'produto-' + productProduto : null)" :productData="productData" :editFull="!!productProduto" />
  </section>
</template>

<script>
import logger from '../../logger'
import { getGrouped, getProductGroupedById, remove } from '../api/product'
import Product from './Product.vue'

export default {
  name: 'products',
  components: { Product },
  props: ['id'],
  data () {
    return {
      loading: true,
      product: null,
      productProduto: null,
      products: [],
      productData: null
    }
  },
  watch: {
    async '$route' (value) {
      const produtoId = value.params['produtoId']
      this.productProduto = produtoId || null
      this.product = value.params['id'] || null
      if (!produtoId && !value.params['id']) {
        this.getData()
      } else if (produtoId) {
        try {
          const prod = this.products.find(p => String(p.id) === String(produtoId))
          this.productData = prod || await getProductGroupedById(produtoId)
        } catch (e) {
          logger.error(e, this)
        }
      } else {
        this.productData = this.findPeriodData(Number(value.params['id']))
      }
    }
  },
  async mounted () {
    this.getData()
  },
  methods: {
    findPeriodData (periodoId) {
      for (const prod of this.products) {
        const per = (prod.periods || []).find(p => p.id === periodoId)
        if (per) {
          const pName = per.periodName || per.period_name || 'Período'
          return {
            id: per.id,
            name: prod.name + ' - ' + pName,
            desc: prod.desc,
            dias: per.dias,
            price: per.price,
            priceUsd: per.priceUsd,
            active: prod.active,
            promotion: per.promotion,
            type: per.type,
            discount: per.discount,
            expiration: per.expiration,
            pf: prod.pf,
            tv: prod.tv,
            nj: prod.nj,
            ba: prod.ba
          }
        }
      }
      return null
    },
    periodLabel (per) {
      const name = per.periodName || per.period_name || 'Período'
      const dias = per.dias != null ? per.dias : null
      return dias != null ? `${name} - (${dias} dias)` : name
    },
    platformLabel (platform) {
      const labels = {
        profit: 'Profit',
        blackarrow: 'BlackArrow',
        ninjatrader: 'NinjaTrader',
        tradingview: 'TradingView',
        profit_yan: 'Profit Yan',
        blackarrow_yan: 'BlackArrow Yan',
        ninjatrader_yan: 'NinjaTrader Yan'
      }
      return labels[platform] || platform || '-'
    },
    promotionPrice (per) {
      const price = Number(per.price) || 0
      const discount = Number(per.discount) || 0
      if (per.type === 'percent') return Math.max(0, price * (1 - discount / 100))
      if (per.type === 'fixed') return Math.max(0, price - discount)
      return price
    },
    promotionPriceUsd (per) {
      const priceUsd = Number(per.priceUsd) || 0
      const discount = Number(per.discount) || 0
      if (per.type === 'percent') return Math.max(0, priceUsd * (1 - discount / 100))
      if (per.type === 'fixed') return Math.max(0, priceUsd - discount)
      return priceUsd
    },
    editProduct (prod) {
      this.productData = prod
      this.productProduto = prod.id
      this.$router.push(`/product/produto/${prod.id}`)
    },
    async getData () {
      try {
        this.product = this.id || null
        this.productProduto = this.$route.params.produtoId || null
        this.products = await getGrouped()
        if (this.productProduto) {
          const prod = this.products.find(p => String(p.id) === String(this.productProduto))
          this.productData = prod || await getProductGroupedById(this.productProduto)
        } else if (this.product) {
          this.productData = this.findPeriodData(Number(this.product))
        }
        this.loading = false
      } catch (error) {
        logger.error(error, this)
      }
    },
    async remove (id) {
      try {
        if (this.loading) return false
        this.loading = true
        // Update user
        await remove(id)
        this.loading = false
        this.getData()
      } catch (error) {
        logger.error(error, this)
        this.loading = false
      }
    }
  }
}
</script>
<style lang="scss" scoped>
@import "../_asset/scss/_variables";
.list {
  border: 1px solid #918f8f;
  background: #e8e8e8;
}
.product-row {
  padding: 12px 16px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}
.product-content {
  flex: 1;
  min-width: 0;
}
.product-info {
  margin-bottom: 8px;
}
.product-platform {
  font-weight: 600;
  font-size: 1.05em;
  color: #333;
}
.product-status {
  margin-left: 8px;
  font-size: 0.8em;
  font-weight: 600;
  color: #0a0;
}
.product-status.inactive {
  color: #c00;
}
.product-desc {
  font-size: 0.9em;
  color: #555;
  margin-top: 2px;
}
.button-edit {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid #999;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  color: #555;
}
.button-edit:hover {
  background: #f0f0f0;
  color: #000;
}
.periods-list {
  padding-left: 16px;
  font-size: 0.9em;
  color: #666;
  line-height: 1.6;
}
.period-line {
  margin-bottom: 4px;
  display: flex;
  gap: 12px;
  align-items: baseline;
  flex-wrap: wrap;
}
.period-name {
  font-weight: 500;
  min-width: 100px;
}
.period-validity {
  color: #666;
  font-size: 0.95em;
}
.period-values {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
}
.period-value {
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.currency-flag {
  width: 15px !important;
  height: 15px !important;
  object-fit: contain;
  vertical-align: middle;
}
.price-original {
  text-decoration: line-through;
  color: #888;
  margin-right: 4px;
}
.price-promo {
  color: #0a0;
  font-weight: 600;
}
</style>
